import { useWeekdayLabel } from '@/shared/components'
import { AdminRequisitFarmListPayload } from 'syncra-api-client'

import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { selectBankNameById } from '@/entities/banks'
import { CalendarIcon, ClockIcon, CreditCard, CircleDollarSign } from 'lucide-react'
import { getBankIconByName } from 'syncra-storybook/shared/utils'
import { formatterRub } from '@/shared/utils'

interface FarmWorkIndicatorsProps {
  data: AdminRequisitFarmListPayload
}

export const FarmWorkIndicators = ({ data }: FarmWorkIndicatorsProps) => {
  const subtitleClasses = 'flex items-center gap-2 text-[#667085] text-sm font-medium'

  const getWorkingHours = () => {
    const start = data.farm?.workHoursStartsAt
    const end = data.farm?.workHoursEndsAt

    return `${start || '00:00:00'} - ${end || '23:59:59'} по UTC`
  }

  const label = useWeekdayLabel(data.farm!.dayOfWeek ?? 0)

  const getLimits = () => {
    const min = formatterRub.format(data.farm?.minDealAmount ?? 0)
    const max = formatterRub.format(data.farm?.maxDealAmount ?? 0)

    if (!min && !max) return null

    const currencyCode = data.farm?.currency === 643 ? 'RUB' : 'USD'
    return `${min || 0} - ${max || 0} ${currencyCode}`
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        {data.banks && data.banks.length > 0 && (
          <div className="flex items-center pr-[7px]">
            {data.banks.slice(0, 3).map((bank, index) => {
              if (!bank.id) return null

              const BankIconComponent = ({ bankId }: { bankId: string }) => {
                const bankName = useSelector((state: RootState) =>
                  selectBankNameById(state, bankId)
                )
                const bankData = getBankIconByName(bankName || '')
                return (
                  <div className="w-5 h-5 flex items-center justify-center" title={bankName}>
                    {bankData.icon}
                  </div>
                )
              }

              return (
                <div
                  key={bank.id}
                  className="w-5 h-5 rounded-full bg-white dark:bg-gray-800 border border-white dark:border-gray-700 -mr-[7px] flex items-center justify-center overflow-hidden"
                  style={{ zIndex: data.banks!.length - index }}
                >
                  <BankIconComponent bankId={bank.id} />
                </div>
              )
            })}
          </div>
        )}

        <span className={subtitleClasses} title="Количество карт">
          <CreditCard className="w-5 h-5 text-primary" />
          {formatterRub.format(data.requisitsCount || 0)}
        </span>

        {getLimits() && (
          <span className={subtitleClasses} title="Сумма сделки">
            <CircleDollarSign className="w-5 h-5 text-primary" />
            {getLimits()}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <span className={subtitleClasses} title="Дни работы">
          <CalendarIcon className="w-5 h-5 text-primary" /> {label}
        </span>
        {getWorkingHours() && (
          <span className={subtitleClasses} title="Время работы">
            <ClockIcon className="w-5 h-5 text-primary" />
            {getWorkingHours()}
          </span>
        )}
      </div>
    </>
  )
}
